import { Button, Card, Group, Stack, Table, Text } from '@mantine/core'
import { IconWifi } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { OnlineStatus } from '@/components/devices/OnlineStatus/OnlineStatus'
import { useGetDeviceMetrics } from '@/queries/deviceQueries'
import { Device } from '@/types/device'
import { getRelativeTimeFromNow } from '@/utils/date'
import { getDeviceAttr } from '@/utils/device/getDeviceAttr'
import { useDevicePing } from './useDevicePing'

type DeploymentDeviceInfoProps = {
  device: Device
  onRefresh: () => void
}

export const DeploymentDeviceInfo = ({
  device,
  onRefresh
}: DeploymentDeviceInfoProps) => {
  const lastActivityTime = getDeviceAttr<number>(device, 'lastActivityTime')
  const lastConnectTime = getDeviceAttr<number>(device, 'lastConnectTime')
  const lastDisconnectTime = getDeviceAttr<number>(device, 'lastDisconnectTime')

  const { data: uptimeData } = useGetDeviceMetrics(device.id, {
    keys: 'uptime_human',
    limit: 1
  })
  const uptime = uptimeData?.['uptime_human']?.[0]?.value ?? null

  const { isPinging, pingDevice } = useDevicePing({
    deviceId: device.id,
    onSuccess: onRefresh
  })

  return (
    <Card padding="md" withBorder>
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <Text fw={600} size="sm">
              {device.label || device.name}
            </Text>
            <OnlineStatus
              status={device.active ? 'online' : 'offline'}
              withText={false}
            />
          </Group>

          <Button
            leftSection={<IconWifi size={14} />}
            loading={isPinging}
            size="xs"
            onClick={() => void pingDevice()}
          >
            <FormattedMessage id="deployments.details.device.ping" />
          </Button>
        </Group>

        <Table variant="vertical" layout="fixed">
          <Table.Tbody>
            <Table.Tr>
              <Table.Td colSpan={2} p={0} pb={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  <FormattedMessage id="devices.fields.edgeClient" />
                </Text>
              </Table.Td>
            </Table.Tr>

            {device.active ? (
              <Table.Tr>
                <Table.Th>
                  <FormattedMessage id="devices.fields.onlineSince" />
                </Table.Th>
                <Table.Td>
                  {lastConnectTime
                    ? getRelativeTimeFromNow(lastConnectTime)
                    : '-'}
                </Table.Td>
              </Table.Tr>
            ) : (
              <Table.Tr>
                <Table.Th>
                  <FormattedMessage id="devices.fields.offlineSince" />
                </Table.Th>
                <Table.Td>
                  {lastDisconnectTime
                    ? getRelativeTimeFromNow(lastDisconnectTime)
                    : '-'}
                </Table.Td>
              </Table.Tr>
            )}

            <Table.Tr>
              <Table.Th>
                <FormattedMessage id="deployments.details.device.lastActivity" />
              </Table.Th>
              <Table.Td>
                {lastActivityTime
                  ? getRelativeTimeFromNow(lastActivityTime)
                  : '-'}
              </Table.Td>
            </Table.Tr>

            <Table.Tr>
              <Table.Td colSpan={2} p={0} pb={4} pt={8}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  <FormattedMessage id="devices.fields.device" />
                </Text>
              </Table.Td>
            </Table.Tr>

            <Table.Tr>
              <Table.Th>
                <FormattedMessage id="devices.fields.uptime" />
              </Table.Th>
              <Table.Td>{uptime ?? '-'}</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  )
}
